import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PageLayout } from '../components/page-layout';
import { MoonLoader } from '../components/ui/moon-loader';
import { formatErrorMessage } from '../lib/utils';
import { 
  Building2, 
  Plus, 
  Users, 
  Calendar,
  Edit,
  Trash2,
  Upload,
  Mail,
  Phone,
  MapPin,
  Globe,
  FileText,
  CreditCard,
  Settings,
  Eye,
  Save,
  X,
  Camera,
  Building,
  Briefcase,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  Info,
  RefreshCw
} from 'lucide-react';
import { apiService } from '../lib/api';
import { useAuth } from '../contexts/auth-context';
import { useToast } from '../hooks/use-toast';
import { TranslatedText } from '../components/translated-text';
import { useTranslation } from '../hooks/useTranslation';
import { CompanyUserAccess } from '../components/company-user-access';
import { getCompanyId } from '../lib/config';

interface Company {
  id: string;
  name: string;
  tenantId?: string;
  logoUrl?: string;
  email?: string;
  phone?: string;
  website?: string;
  // Support both flat and nested address structures
  address?: string | {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
  };
  // Flat address fields from API
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  businessType?: string;
  industry?: string;
  taxId?: string;
  registrationNumber?: string;
  fiscalYearStart?: string;
  currency?: string;
  timezone?: string;
  description?: string;
  employees?: number;
  foundedYear?: number;
  status?: 'active' | 'inactive' | 'suspended';
  settings?: {
    allowMultipleCurrencies?: boolean;
    enableTaxCalculation?: boolean;
    enableInventoryTracking?: boolean;
    autoBackup?: boolean;
  };
  createdAt?: string;
  updatedAt?: string;
  companySettings?: Array<{
    id: string;
    key: string;
    value: string;
  }>;
}

export default function CompaniesPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const { tSync } = useTranslation();
  const queryClient = useQueryClient();
  
  // Check if user is admin - only admins can create companies
  const userRole = user?.role
  const isAdmin = !authLoading && user !== null && userRole && typeof userRole === 'string' && userRole.toLowerCase() === 'admin'
  
  // Use React Query for companies data
  const { data: companiesData, isLoading: loading, error } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await apiService.getCompanies({ page: 1, pageSize: 50 });
      
      // Handle different response structures
      let companiesArray: any[] = [];
      if (Array.isArray(response)) {
        companiesArray = response;
      } else if (response && typeof response === 'object') {
        const responseAny = response as any;
        // Check for data property (most common)
        if (Array.isArray(responseAny.data)) {
          companiesArray = responseAny.data;
        } else if (Array.isArray(responseAny.companies)) {
          companiesArray = responseAny.companies;
        } else if (Array.isArray(responseAny.items)) {
          companiesArray = responseAny.items;
        } else if (responseAny.data && typeof responseAny.data === 'object' && !Array.isArray(responseAny.data)) {
          // If data is an object, it might be a single company wrapped
          companiesArray = [responseAny.data];
        }
      }
      
      
      // Process companies to extract fields from companySettings
      const processedCompanies = companiesArray.map((company: any) => {
        
        if (company.companySettings && Array.isArray(company.companySettings)) {
          const settings = company.companySettings;
          
          // Extract fields from settings and add them to the company object
          const processed = {
            ...company,
            status: settings.find((s: any) => s.key === 'status')?.value || 'active',
            description: settings.find((s: any) => s.key === 'description')?.value || '',
            employees: parseInt(settings.find((s: any) => s.key === 'employees')?.value || '0'),
            foundedYear: parseInt(settings.find((s: any) => s.key === 'foundedYear')?.value || new Date().getFullYear().toString()),
            businessType: settings.find((s: any) => s.key === 'businessType')?.value || '',
            registrationNumber: settings.find((s: any) => s.key === 'registrationNumber')?.value || '',
            timezone: settings.find((s: any) => s.key === 'timezone')?.value || 'America/New_York',
            settings: {
              allowMultipleCurrencies: settings.find((s: any) => s.key === 'allowMultipleCurrencies')?.value === 'true',
              enableTaxCalculation: settings.find((s: any) => s.key === 'enableTaxCalculation')?.value === 'true',
              enableInventoryTracking: settings.find((s: any) => s.key === 'enableInventoryTracking')?.value === 'true',
              autoBackup: settings.find((s: any) => s.key === 'autoBackup')?.value === 'true',
            }
          };
          
          return processed;
        }
        
        return company;
      });
      
      
      return processedCompanies;
    },
    enabled: !!user
  });

  const companies = companiesData || [];

  // Log error if present
  useEffect(() => {
    if (error) {
      console.error('❌ Companies fetch error:', error);
    }
    if (companiesData) {
    }
  }, [error, companiesData, companies.length]);

  // State for modals and forms
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('companies');
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Loading states for company operations
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [isUpdatingCompany, setIsUpdatingCompany] = useState(false);
  const [formData, setFormData] = useState<Partial<Company>>({
    name: '',
    email: '',
    phone: '',
    website: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
    },
    businessType: '',
    industry: '',
    taxId: '',
    registrationNumber: '',
    fiscalYearStart: 'January',
    currency: 'USD',
    timezone: 'America/New_York',
    description: '',
    employees: 0,
    foundedYear: new Date().getFullYear(),
    status: 'active',
    tenantId: user?.tenantId,
    settings: {
      allowMultipleCurrencies: false,
      enableTaxCalculation: true,
      enableInventoryTracking: false,
      autoBackup: true,
    }
  });

  // React Query mutations for create/update/delete
  const createCompanyMutation = useMutation({
    mutationFn: async (data: Partial<Company>) => {
      const filteredFormData = { ...data };
      
      // Remove empty address fields to avoid validation errors
      if (filteredFormData.address && typeof filteredFormData.address === 'object') {
        const address = filteredFormData.address as any;
        if (!address.street?.trim()) delete address.street;
        if (!address.city?.trim()) delete address.city;
        if (!address.state?.trim()) delete address.state;
        if (!address.country?.trim()) delete address.country;
        if (!address.zipCode?.trim()) delete address.zipCode;
        
        // If all address fields are empty, remove the entire address object
        if (!address.street && !address.city && !address.state && !address.country && !address.zipCode) {
          delete filteredFormData.address;
        }
      }
      
      // Remove other empty fields
      Object.keys(filteredFormData).forEach(key => {
        if (filteredFormData[key as keyof Company] === '' || filteredFormData[key as keyof Company] === null) {
          delete filteredFormData[key as keyof Company];
        }
      });
      
      return await apiService.createCompany(filteredFormData);
    },
    onSuccess: async (response, variables) => {
      // Upload logo if one was selected
      if (selectedLogoFile && response.id) {
        try {
          await apiService.uploadCompanyLogo(response.id, selectedLogoFile);
        } catch (logoError) {
          // Don't fail the entire operation if logo upload fails
        }
      }
      
      // Invalidate and refetch companies
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      
      setIsCreateModalOpen(false);
      resetForm();
      toast({ title: tSync("Company Created"), description: tSync("Company has been created successfully.") });
    },
    onError: (err: any) => {
      console.error('Error creating company:', err);
      toast({ 
        title: tSync("Error"), 
        description: err.message || tSync("Failed to create company"),
        variant: "destructive"
      });
    }
  });

  const updateCompanyMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Company> }) => {
      const filteredFormData = { ...data };
      
      // Remove empty address fields to avoid validation errors
      if (filteredFormData.address && typeof filteredFormData.address === 'object') {
        const address = filteredFormData.address as any;
        if (!address.street?.trim()) delete address.street;
        if (!address.city?.trim()) delete address.city;
        if (!address.state?.trim()) delete address.state;
        if (!address.country?.trim()) delete address.country;
        if (!address.zipCode?.trim()) delete address.zipCode;
        
        // If all address fields are empty, remove the entire address object
        if (!address.street && !address.city && !address.state && !address.country && !address.zipCode) {
          delete filteredFormData.address;
        }
      }
      
      // Remove other empty fields
      Object.keys(filteredFormData).forEach(key => {
        if (filteredFormData[key as keyof Company] === '' || filteredFormData[key as keyof Company] === null) {
          delete filteredFormData[key as keyof Company];
        }
      });
      
      return await apiService.updateCompany(id, filteredFormData);
    },
    onSuccess: async (response, variables) => {
      // Upload logo if one was selected
      if (selectedLogoFile) {
        try {
          await apiService.uploadCompanyLogo(variables.id, selectedLogoFile);
        } catch (logoError) {
          // Don't fail the entire operation if logo upload fails
        }
      }
      
      // Invalidate and refetch companies
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      
      setIsEditModalOpen(false);
      setSelectedCompany(null);
      resetForm();
      toast({ title: tSync("Company Updated"), description: tSync("Company has been updated successfully.") });
    },
    onError: (err: any) => {
      console.error('Error updating company:', err);
      toast({ 
        title: tSync("Error"), 
        description: err.message || tSync("Failed to update company"),
        variant: "destructive"
      });
    }
  });

  const deactivateCompanyMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'active' | 'inactive' }) => {
      return await apiService.updateCompany(id, { status } as any);
    },
    onSuccess: async (_, variables) => {
      // Invalidate and refetch companies
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast({ 
        title: variables.status === 'inactive' ? tSync("Company Deactivated") : tSync("Company Activated"), 
        description: variables.status === 'inactive' 
          ? tSync("Company has been deactivated successfully.") 
          : tSync("Company has been activated successfully.") 
      });
    },
    onError: (err: any) => {
      // Extract error message from various possible structures
      let errorMessage = err.message || err.error?.message || err.response?.data?.message || tSync("Failed to update company status");
      
      toast({ 
        title: tSync("Error"), 
        description: errorMessage,
        variant: "destructive"
      });
    }
  });

  const handleCreateCompany = async () => {
    try {
      setIsCreatingCompany(true);
      
      // Filter out empty fields to avoid validation errors
      const filteredFormData = { ...formData };
      
      // Remove empty address fields
      if (filteredFormData.address && typeof filteredFormData.address === 'object') {
        const address = filteredFormData.address;
        if (!address.street) delete address.street;
        if (!address.city) delete address.city;
        if (!address.state) delete address.state;
        if (!address.country) delete address.country;
        if (!address.zipCode) delete address.zipCode;
        
        // If all address fields are empty, remove the address object entirely
        if (Object.keys(address).length === 0) {
          delete filteredFormData.address;
        }
      }
      
      // Remove other empty fields
      Object.keys(filteredFormData).forEach(key => {
        const value = filteredFormData[key as keyof typeof filteredFormData];
        if (value === '' || value === null || value === undefined) {
          delete filteredFormData[key as keyof typeof filteredFormData];
        }
      });
      
      const response = await apiService.createCompany(filteredFormData);
      
      // Upload logo if one was selected
      if (selectedLogoFile && response.id) {
        try {
          await apiService.uploadCompanyLogo(response.id, selectedLogoFile);
        } catch (logoError) {
          // Don't fail the entire operation if logo upload fails
        }
      }
      
      // Refresh the companies list to get the complete data with all fields
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      setIsCreateModalOpen(false);
      resetForm();
      toast({
        title: tSync("Company Created"),
        description: tSync("Company has been created successfully."),
      });
    } catch (err: any) {
      console.error('Error creating company:', err);
      toast({
        title: tSync("Error"),
        description: err.message || tSync("Failed to create company"),
        variant: "destructive",
      });
    } finally {
      setIsCreatingCompany(false);
    }
  };

  const handleUpdateCompany = async () => {
    if (!selectedCompany) return;
    
    try {
      setIsUpdatingCompany(true);
      
      // Filter out empty fields to avoid validation errors
      const filteredFormData = { ...formData };
      
      // Remove empty address fields
      if (filteredFormData.address && typeof filteredFormData.address === 'object') {
        const address = filteredFormData.address;
        if (!address.street) delete address.street;
        if (!address.city) delete address.city;
        if (!address.state) delete address.state;
        if (!address.country) delete address.country;
        if (!address.zipCode) delete address.zipCode;
        
        // If all address fields are empty, remove the address object entirely
        if (Object.keys(address).length === 0) {
          delete filteredFormData.address;
        }
      }
      
      // Remove other empty fields
      Object.keys(filteredFormData).forEach(key => {
        const value = filteredFormData[key as keyof typeof filteredFormData];
        if (value === '' || value === null || value === undefined) {
          delete filteredFormData[key as keyof typeof filteredFormData];
        }
      });
      
      const response = await apiService.updateCompany(selectedCompany.id, filteredFormData);
      
      // Upload logo if one was selected
      if (selectedLogoFile) {
        try {
          await apiService.uploadCompanyLogo(selectedCompany.id, selectedLogoFile);
        } catch (logoError) {
          // Don't fail the entire operation if logo upload fails
        }
      }
      
      // Refresh the companies list to get the complete data with all fields
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      setIsEditModalOpen(false);
      setSelectedCompany(null);
      resetForm();
      toast({
        title: tSync("Company Updated"),
        description: tSync("Company has been updated successfully."),
      });
    } catch (err: any) {
      console.error('Error updating company:', err);
      toast({
        title: tSync("Error"),
        description: err.message || tSync("Failed to update company"),
        variant: "destructive",
      });
    } finally {
      setIsUpdatingCompany(false);
    }
  };

  const handleDeactivateCompany = async (companyId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    deactivateCompanyMutation.mutate({ id: companyId, status: newStatus as 'active' | 'inactive' });
  };

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: tSync("Invalid File Type"),
          description: tSync("Please select a valid image file (JPEG, PNG, GIF, or WebP)."),
          variant: "destructive",
        });
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: tSync("File Too Large"),
          description: tSync("Please select an image smaller than 5MB."),
          variant: "destructive",
        });
        return;
      }
      
      setSelectedLogoFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };

  const handleLogoUpload = async (file?: File) => {
    const logoFile = file || selectedLogoFile;
    if (!logoFile || !selectedCompany) return;
    
    try {
      setUploadingLogo(true);
      const response = await apiService.uploadCompanyLogo(selectedCompany.id, logoFile);
      
      
      // Invalidate and refetch companies to get updated logo
      await queryClient.invalidateQueries({ queryKey: ['companies'] });
      
      // Clear the logo selection
      setSelectedLogoFile(null);
      setLogoPreview(null);
      
      toast({
        title: tSync("Logo Uploaded"),
        description: tSync("Company logo has been uploaded successfully."),
      });
    } catch (err: any) {
      toast({
        title: tSync("Upload Failed"),
        description: err.message || tSync("Failed to upload logo"),
        variant: "destructive",
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      website: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
      },
      businessType: '',
      industry: '',
      taxId: '',
      registrationNumber: '',
      fiscalYearStart: 'January',
      currency: 'USD',
      timezone: 'America/New_York',
      description: '',
      employees: 0,
      foundedYear: new Date().getFullYear(),
      status: 'active',
      settings: {
        allowMultipleCurrencies: false,
        enableTaxCalculation: true,
        enableInventoryTracking: true,
        autoBackup: true,
      }
    });
    setSelectedLogoFile(null);
    setLogoPreview(null);
  };

  const openEditModal = (company: Company) => {
    setSelectedCompany(company);
    
    // Extract settings from companySettings array if it exists
    let settings = {
      allowMultipleCurrencies: false,
      enableTaxCalculation: true,
      enableInventoryTracking: false,
      autoBackup: true,
    };
    
    if ((company as any).companySettings && Array.isArray((company as any).companySettings)) {
      const companySettings = (company as any).companySettings;
      settings = {
        allowMultipleCurrencies: companySettings.find((s: any) => s.key === 'allowMultipleCurrencies')?.value === 'true',
        enableTaxCalculation: companySettings.find((s: any) => s.key === 'enableTaxCalculation')?.value === 'true',
        enableInventoryTracking: companySettings.find((s: any) => s.key === 'enableInventoryTracking')?.value === 'true',
        autoBackup: companySettings.find((s: any) => s.key === 'autoBackup')?.value === 'true',
      };
    }
    
    // Extract additional fields from companySettings
    const description = (company as any).companySettings?.find((s: any) => s.key === 'description')?.value || '';
    const employees = parseInt((company as any).companySettings?.find((s: any) => s.key === 'employees')?.value || '0');
    const foundedYear = parseInt((company as any).companySettings?.find((s: any) => s.key === 'foundedYear')?.value || new Date().getFullYear().toString());
    const status = (company as any).companySettings?.find((s: any) => s.key === 'status')?.value || 'active';
    const businessType = (company as any).companySettings?.find((s: any) => s.key === 'businessType')?.value || '';
    const registrationNumber = (company as any).companySettings?.find((s: any) => s.key === 'registrationNumber')?.value || '';
    const timezone = (company as any).companySettings?.find((s: any) => s.key === 'timezone')?.value || 'America/New_York';
    
    // Only set the fields that should be editable, not the entire company object
    setFormData({
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      website: company.website || '',
      address: {
        street: typeof company.address === 'string' ? company.address : company.address?.street || '',
        city: company.city || '',
        state: company.state || '',
        country: company.country || '',
        zipCode: company.postalCode || '',
      },
      businessType,
      industry: company.industry || '',
      taxId: company.taxId || '',
      registrationNumber,
      fiscalYearStart: company.fiscalYearStart || 'January',
      currency: company.currency || 'USD',
      timezone,
      description,
      employees,
      foundedYear,
      status,
      settings
    });
    setIsEditModalOpen(true);
  };

  const openViewModal = (company: Company) => {
    setSelectedCompany(company);
    setIsViewModalOpen(true);
  };

  const renderCompanyForm = () => (
    <div className="space-y-6">
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="basic"><TranslatedText text="Basic Info" /></TabsTrigger>
          <TabsTrigger value="contact"><TranslatedText text="Contact" /></TabsTrigger>
          <TabsTrigger value="business"><TranslatedText text="Business" /></TabsTrigger>
          <TabsTrigger value="settings"><TranslatedText text="Settings" /></TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name"><TranslatedText text="Company Name" /> *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={tSync("Enter company name")}
                required
              />
            </div>
            
            {/* Logo Upload Section */}
            <div className="col-span-2 space-y-4">
              <Label><TranslatedText text="Company Logo" /></Label>
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Handle preview error silently
                      }}
                    />
                  ) : selectedCompany?.logoUrl && selectedCompany.logoUrl.trim() !== '' ? (
                    <img 
                      src={selectedCompany.logoUrl} 
                      alt="Current logo" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                      }}
                      onLoad={() => {
                      }}
                    />
                  ) : (
                    <FileText className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleLogoSelect}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('logo-upload')?.click()}
                      disabled={uploadingLogo}
                    >
                          {uploadingLogo ? (
                        <>
                          <MoonLoader size="sm" className="mr-2" color="teal" />
                          <TranslatedText text="Uploading..." />
                        </>
                      ) : (
                        <>
                          <Camera className="w-4 h-4 mr-2" />
                          {selectedLogoFile ? <TranslatedText text="Change Logo" /> : <TranslatedText text="Upload Logo" />}
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <TranslatedText text="Select a company logo (JPEG, PNG, GIF, or WebP, max 5MB). Logo will be saved when you submit the form." />
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="industry"><TranslatedText text="Industry" /></Label>
              <Select value={formData.industry} onValueChange={(value) => setFormData({...formData, industry: value})}>
                <SelectTrigger>
                  <SelectValue placeholder={tSync("Select industry")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technology"><TranslatedText text="Technology" /></SelectItem>
                  <SelectItem value="finance"><TranslatedText text="Finance" /></SelectItem>
                  <SelectItem value="healthcare"><TranslatedText text="Healthcare" /></SelectItem>
                  <SelectItem value="retail"><TranslatedText text="Retail" /></SelectItem>
                  <SelectItem value="manufacturing"><TranslatedText text="Manufacturing" /></SelectItem>
                  <SelectItem value="consulting"><TranslatedText text="Consulting" /></SelectItem>
                  <SelectItem value="other"><TranslatedText text="Other" /></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description"><TranslatedText text="Description" /></Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder={tSync("Brief description of the company")}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="employees"><TranslatedText text="Employees" /></Label>
              <Input
                id="employees"
                type="number"
                value={formData.employees}
                onChange={(e) => setFormData({...formData, employees: parseInt(e.target.value) || 0})}
                placeholder={tSync("Number of employees")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="foundedYear"><TranslatedText text="Founded Year" /></Label>
              <Input
                id="foundedYear"
                type="number"
                value={formData.foundedYear}
                onChange={(e) => setFormData({...formData, foundedYear: parseInt(e.target.value) || new Date().getFullYear()})}
                placeholder={tSync("Year founded")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status"><TranslatedText text="Status" /></Label>
              <Select value={formData.status} onValueChange={(value: 'active' | 'inactive' | 'suspended') => setFormData({...formData, status: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active"><TranslatedText text="Active" /></SelectItem>
                  <SelectItem value="inactive"><TranslatedText text="Inactive" /></SelectItem>
                  <SelectItem value="suspended"><TranslatedText text="Suspended" /></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contact" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email"><TranslatedText text="Email" /></Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder={tSync("company@example.com")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone"><TranslatedText text="Phone" /></Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder={tSync("+1 (555) 123-4567")}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website"><TranslatedText text="Website" /></Label>
            <Input
              id="website"
              value={formData.website}
              onChange={(e) => setFormData({...formData, website: e.target.value})}
              placeholder={tSync("https://www.company.com")}
            />
          </div>

          <div className="space-y-4">
            <Label><TranslatedText text="Address" /></Label>
            <div className="space-y-2">
              <Input
                value={typeof formData.address === 'object' ? formData.address?.street || '' : ''}
                onChange={(e) => setFormData({...formData, address: {...(typeof formData.address === 'object' ? formData.address : {}), street: e.target.value}})}
                placeholder={tSync("Street address")}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={typeof formData.address === 'object' ? formData.address?.city || '' : ''}
                  onChange={(e) => setFormData({...formData, address: {...(typeof formData.address === 'object' ? formData.address : {}), city: e.target.value}})}
                  placeholder={tSync("City")}
                />
                <Input
                  value={typeof formData.address === 'object' ? formData.address?.state || '' : ''}
                  onChange={(e) => setFormData({...formData, address: {...(typeof formData.address === 'object' ? formData.address : {}), state: e.target.value}})}
                  placeholder={tSync("State/Province")}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={typeof formData.address === 'object' ? formData.address?.country || '' : ''}
                  onChange={(e) => setFormData({...formData, address: {...(typeof formData.address === 'object' ? formData.address : {}), country: e.target.value}})}
                  placeholder={tSync("Country")}
                />
                <Input
                  value={typeof formData.address === 'object' ? formData.address?.zipCode || '' : ''}
                  onChange={(e) => setFormData({...formData, address: {...(typeof formData.address === 'object' ? formData.address : {}), zipCode: e.target.value}})}
                  placeholder={tSync("ZIP/Postal Code")}
                />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="business" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="businessType"><TranslatedText text="Business Type" /></Label>
              <Select value={formData.businessType} onValueChange={(value) => setFormData({...formData, businessType: value})}>
                <SelectTrigger>
                  <SelectValue placeholder={tSync("Select business type")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="corporation"><TranslatedText text="Corporation" /></SelectItem>
                  <SelectItem value="llc"><TranslatedText text="LLC" /></SelectItem>
                  <SelectItem value="partnership"><TranslatedText text="Partnership" /></SelectItem>
                  <SelectItem value="sole-proprietorship"><TranslatedText text="Sole Proprietorship" /></SelectItem>
                  <SelectItem value="nonprofit"><TranslatedText text="Non-Profit" /></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency"><TranslatedText text="Currency" /></Label>
              <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="taxId"><TranslatedText text="Tax ID" /></Label>
              <Input
                id="taxId"
                value={formData.taxId}
                onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                placeholder={tSync("Enter tax identification number")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registrationNumber"><TranslatedText text="Registration Number" /></Label>
              <Input
                id="registrationNumber"
                value={formData.registrationNumber}
                onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                placeholder={tSync("Enter registration number")}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fiscalYearStart"><TranslatedText text="Fiscal Year Start" /></Label>
              <Select value={formData.fiscalYearStart} onValueChange={(value) => setFormData({...formData, fiscalYearStart: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="January"><TranslatedText text="January" /></SelectItem>
                  <SelectItem value="February"><TranslatedText text="February" /></SelectItem>
                  <SelectItem value="March"><TranslatedText text="March" /></SelectItem>
                  <SelectItem value="April"><TranslatedText text="April" /></SelectItem>
                  <SelectItem value="May"><TranslatedText text="May" /></SelectItem>
                  <SelectItem value="June"><TranslatedText text="June" /></SelectItem>
                  <SelectItem value="July"><TranslatedText text="July" /></SelectItem>
                  <SelectItem value="August"><TranslatedText text="August" /></SelectItem>
                  <SelectItem value="September"><TranslatedText text="September" /></SelectItem>
                  <SelectItem value="October"><TranslatedText text="October" /></SelectItem>
                  <SelectItem value="November"><TranslatedText text="November" /></SelectItem>
                  <SelectItem value="December"><TranslatedText text="December" /></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="timezone"><TranslatedText text="Timezone" /></Label>
              <Select value={formData.timezone} onValueChange={(value) => setFormData({...formData, timezone: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York"><TranslatedText text="Eastern Time" /></SelectItem>
                  <SelectItem value="America/Chicago"><TranslatedText text="Central Time" /></SelectItem>
                  <SelectItem value="America/Denver"><TranslatedText text="Mountain Time" /></SelectItem>
                  <SelectItem value="America/Los_Angeles"><TranslatedText text="Pacific Time" /></SelectItem>
                  <SelectItem value="Europe/London"><TranslatedText text="GMT" /></SelectItem>
                  <SelectItem value="Europe/Paris"><TranslatedText text="CET" /></SelectItem>
                  <SelectItem value="Asia/Tokyo"><TranslatedText text="JST" /></SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label><TranslatedText text="Multiple Currencies" /></Label>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Allow transactions in multiple currencies" /></p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFormData({
                  ...formData, 
                  settings: {...formData.settings, allowMultipleCurrencies: !formData.settings?.allowMultipleCurrencies}
                })}
              >
                {formData.settings?.allowMultipleCurrencies ? <TranslatedText text="Enabled" /> : <TranslatedText text="Disabled" />}
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label><TranslatedText text="Tax Calculation" /></Label>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Automatically calculate taxes on transactions" /></p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFormData({
                  ...formData, 
                  settings: {...formData.settings, enableTaxCalculation: !formData.settings?.enableTaxCalculation}
                })}
              >
                {formData.settings?.enableTaxCalculation ? <TranslatedText text="Enabled" /> : <TranslatedText text="Disabled" />}
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label><TranslatedText text="Inventory Tracking" /></Label>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Track inventory levels and movements" /></p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFormData({
                  ...formData, 
                  settings: {...formData.settings, enableInventoryTracking: !formData.settings?.enableInventoryTracking}
                })}
              >
                {formData.settings?.enableInventoryTracking ? <TranslatedText text="Enabled" /> : <TranslatedText text="Disabled" />}
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label><TranslatedText text="Auto Backup" /></Label>
                <p className="text-sm text-muted-foreground"><TranslatedText text="Automatically backup company data" /></p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFormData({
                  ...formData, 
                  settings: {...formData.settings, autoBackup: !formData.settings?.autoBackup}
                })}
              >
                {formData.settings?.autoBackup ? <TranslatedText text="Enabled" /> : <TranslatedText text="Disabled" />}
              </Button>
            </div>
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <MoonLoader size="lg" color="teal" />
            <p className="mt-2 text-muted-foreground"><TranslatedText text="Loading companies..." /></p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    const errorMessage = typeof error === 'object' && error !== null && 'message' in error 
      ? (error as any).message 
      : typeof error === 'string' 
        ? error 
        : 'Unknown error'
    const formattedError = formatErrorMessage(error)
    
    return (
      <PageLayout>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {formattedError}
            <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['companies'] })} className="ml-2" size="sm">
              <TranslatedText text="Try Again" />
            </Button>
          </AlertDescription>
        </Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        {/* Enhanced Header */}
        <div className="bg-gradient-to-r from-cyan-50 via-blue-50 to-indigo-50 p-8 border-b border-cyan-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900"><TranslatedText text="Company Management" /></h1>
                <p className="text-gray-600 mt-1"><TranslatedText text="Manage your companies and organizational settings" /></p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="px-3 py-1">
                <CheckCircle className="w-3 h-3 mr-1" />
                {companies.length} {companies.length === 1 ? <TranslatedText text="Company" /> : <TranslatedText text="Companies" />}
              </Badge>
              {isAdmin && (
                <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white shadow-lg"
                      disabled={isCreatingCompany}
                    >
                      {isCreatingCompany ? (
                        <>
                          <MoonLoader size="sm" color="blue" className="mr-2" />
                          <TranslatedText text="Creating..." />
                        </>
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          <TranslatedText text="Add Company" />
                        </>
                      )}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Plus className="h-5 w-5" />
                        <TranslatedText text="Create New Company" />
                      </DialogTitle>
                      <DialogDescription>
                        <TranslatedText text="Add a new company to your organization. Fill in the required information below." />
                      </DialogDescription>
                    </DialogHeader>
                    {renderCompanyForm()}
                    <div className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={() => {setIsCreateModalOpen(false); resetForm();}}>
                        <TranslatedText text="Cancel" />
                      </Button>
                      <Button onClick={handleCreateCompany} disabled={!formData.name || isCreatingCompany}>
                        {isCreatingCompany ? (
                          <>
                            <MoonLoader size="sm" color="blue" className="mr-2" />
                            <TranslatedText text="Creating..." />
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            <TranslatedText text="Create Company" />
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="companies">
              <Building2 className="w-4 h-4 mr-2" />
              <TranslatedText text="Companies" />
            </TabsTrigger>
            <TabsTrigger value="user-access">
              <Users className="w-4 h-4 mr-2" />
              <TranslatedText text="User Access" />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="companies" className="space-y-4">
        {companies.length === 0 ? (
          <Card className="text-center py-16">
            <CardContent>
              <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
              <h3 className="text-xl font-semibold mb-2"><TranslatedText text="No companies found" /></h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                <TranslatedText text="Get started by creating your first company. You can manage all your organizational settings and preferences from here." />
              </p>
              <Button 
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
                onClick={() => setIsCreateModalOpen(true)}
                disabled={isCreatingCompany}
              >
                {isCreatingCompany ? (
                  <>
                    <MoonLoader size="sm" color="blue" className="mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    <TranslatedText text="Create Your First Company" />
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {companies.map((company: any) => {
              return (
              <Card key={company.id} className="hover:shadow-lg transition-all duration-200 border-0 shadow-md">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg">
                        {company.logoUrl && company.logoUrl.trim() !== '' ? (
                          <img 
                            src={company.logoUrl} 
                            alt={company.name} 
                            className="w-12 h-12 rounded-xl object-cover"
                            onError={(e) => {
                              // Handle logo load error silently
                            }}
                            onLoad={() => {
                              // Logo loaded successfully
                            }}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                            <FileText className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-lg truncate">{company.name}</CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <TranslatedText text="Created" /> {company.createdAt ? new Date(company.createdAt).toLocaleDateString() : 'N/A'}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge 
                      variant={company.status === 'active' ? 'default' : company.status === 'inactive' ? 'destructive' : 'destructive'}
                      className="capitalize"
                    >
                      {company.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {company.industry && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Briefcase className="w-4 h-4 mr-2" />
                      {company.industry}
                    </div>
                  )}
                  {company.email && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Mail className="w-4 h-4 mr-2" />
                      {company.email}
                    </div>
                  )}
                  {company.address && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4 mr-2" />
                      {company.city || ''}, {company.country || ''}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center space-x-2">
                      <Button size="sm" variant="outline" onClick={() => openViewModal(company)}>
                        <Eye className="w-3 h-3 mr-1" />
                        <TranslatedText text="View" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEditModal(company)}>
                        <Edit className="w-3 h-3 mr-1" />
                        <TranslatedText text="Edit" />
                      </Button>
                    </div>
                    {company.status === 'active' ? (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        onClick={() => handleDeactivateCompany(company.id, company.status)}
                        disabled={deactivateCompanyMutation.isPending}
                        style={{ display: isAdmin ? 'inline-flex' : 'none' }}
                        title={tSync("Deactivate Company")}
                      >
                        {deactivateCompanyMutation.isPending ? (
                          <MoonLoader size="sm" color="purple" />
                        ) : (
                          <>
                            <X className="w-3 h-3 mr-1" />
                            <TranslatedText text="Deactivate" />
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        onClick={() => handleDeactivateCompany(company.id, company.status)}
                        disabled={deactivateCompanyMutation.isPending}
                        style={{ display: isAdmin ? 'inline-flex' : 'none' }}
                        title={tSync("Activate Company")}
                      >
                        {deactivateCompanyMutation.isPending ? (
                          <MoonLoader size="sm" color="purple" />
                        ) : (
                          <>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            <TranslatedText text="Activate" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          </div>
        )}
          </TabsContent>

          <TabsContent value="user-access" className="space-y-4">
            {(() => {
              // Get the currently selected company from localStorage (header selection)
              let selectedCompanyId: string | undefined;
              try {
                selectedCompanyId = getCompanyId();
              } catch (error) {
                // If no company is selected, use the first company as fallback
                selectedCompanyId = companies.length > 0 ? companies[0]?.id : undefined;
              }
              
              return selectedCompanyId ? (
                <CompanyUserAccess companyId={selectedCompanyId} />
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>User Access Management</CardTitle>
                    <CardDescription>
                      Please create a company first to manage user access.
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })()}
          </TabsContent>
        </Tabs>

        {/* Edit Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                <TranslatedText text="Edit Company" />
              </DialogTitle>
              <DialogDescription>
                <TranslatedText text="Update company information and settings." />
              </DialogDescription>
            </DialogHeader>
            {renderCompanyForm()}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {setIsEditModalOpen(false); setSelectedCompany(null); resetForm();}}>
                <TranslatedText text="Cancel" />
              </Button>
              <Button onClick={handleUpdateCompany} disabled={isUpdatingCompany}>
                {isUpdatingCompany ? (
                  <>
                    <MoonLoader size="sm" color="blue" className="mr-2" />
                    <TranslatedText text="Saving..." />
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    <TranslatedText text="Save Changes" />
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Modal */}
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                <TranslatedText text="Company Details" />
              </DialogTitle>
              <DialogDescription>
                <TranslatedText text="View detailed information about" /> {selectedCompany?.name}
              </DialogDescription>
            </DialogHeader>
            {selectedCompany && (
              <div className="space-y-6">
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg">
                    {selectedCompany.logoUrl && selectedCompany.logoUrl.trim() !== '' ? (
                      <img 
                        src={selectedCompany.logoUrl} 
                        alt={selectedCompany.name} 
                        className="w-16 h-16 rounded-xl object-cover"
                        onError={(e) => {
                          // Handle logo load error silently
                        }}
                        onLoad={() => {
                          // Logo loaded successfully
                        }}
                      />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                          <FileText className="w-8 h-8 text-white" />
                        </div>
                      )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{selectedCompany.name}</h3>
                    <p className="text-muted-foreground">{selectedCompany.industry}</p>
                    <Badge variant={selectedCompany.status === 'active' ? 'default' : 'destructive'} className="mt-1">
                      {selectedCompany.status}
                    </Badge>
                  </div>
                </div>

                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="overview"><TranslatedText text="Overview" /></TabsTrigger>
                    <TabsTrigger value="contact"><TranslatedText text="Contact" /></TabsTrigger>
                    <TabsTrigger value="business"><TranslatedText text="Business" /></TabsTrigger>
                    <TabsTrigger value="settings"><TranslatedText text="Settings" /></TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Description" /></Label>
                        <p>{selectedCompany.description || <TranslatedText text="No description provided" />}</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Employees" /></Label>
                        <p>{selectedCompany.employees || <TranslatedText text="Not specified" />}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Founded Year" /></Label>
                        <p>{selectedCompany.foundedYear || <TranslatedText text="Not specified" />}</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Created" /></Label>
                        <p>{selectedCompany.createdAt ? new Date(selectedCompany.createdAt).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="contact" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Email" /></Label>
                        <p>{selectedCompany.email || <TranslatedText text="Not provided" />}</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Phone" /></Label>
                        <p>{selectedCompany.phone || <TranslatedText text="Not provided" />}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Website" /></Label>
                      <p>{selectedCompany.website || <TranslatedText text="Not provided" />}</p>
                    </div>
                    {selectedCompany.address && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Address" /></Label>
                        <p>
                          {typeof selectedCompany.address === 'string' ? selectedCompany.address : selectedCompany.address?.street}<br/>
                          {selectedCompany.city}, {selectedCompany.state} {selectedCompany.postalCode}<br/>
                          {selectedCompany.country}
                        </p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="business" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Business Type" /></Label>
                        <p>{selectedCompany.businessType || <TranslatedText text="Not specified" />}</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Currency" /></Label>
                        <p>{selectedCompany.currency || 'USD'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Tax ID" /></Label>
                        <p>{selectedCompany.taxId || <TranslatedText text="Not provided" />}</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Registration Number" /></Label>
                        <p>{selectedCompany.registrationNumber || <TranslatedText text="Not provided" />}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Fiscal Year Start" /></Label>
                        <p>{selectedCompany.fiscalYearStart || 'January'}</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground"><TranslatedText text="Timezone" /></Label>
                        <p>{selectedCompany.timezone || <TranslatedText text="Not specified" />}</p>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="settings" className="space-y-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label className="font-medium"><TranslatedText text="Multiple Currencies" /></Label>
                          <p className="text-sm text-muted-foreground"><TranslatedText text="Allow transactions in multiple currencies" /></p>
                        </div>
                        <Badge variant={selectedCompany.settings?.allowMultipleCurrencies ? 'default' : 'secondary'}>
                          {selectedCompany.settings?.allowMultipleCurrencies ? <TranslatedText text="Enabled" /> : <TranslatedText text="Disabled" />}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label className="font-medium"><TranslatedText text="Tax Calculation" /></Label>
                          <p className="text-sm text-muted-foreground"><TranslatedText text="Automatically calculate taxes" /></p>
                        </div>
                        <Badge variant={selectedCompany.settings?.enableTaxCalculation ? 'default' : 'secondary'}>
                          {selectedCompany.settings?.enableTaxCalculation ? <TranslatedText text="Enabled" /> : <TranslatedText text="Disabled" />}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label className="font-medium"><TranslatedText text="Inventory Tracking" /></Label>
                          <p className="text-sm text-muted-foreground"><TranslatedText text="Track inventory levels" /></p>
                        </div>
                        <Badge variant={selectedCompany.settings?.enableInventoryTracking ? 'default' : 'secondary'}>
                          {selectedCompany.settings?.enableInventoryTracking ? <TranslatedText text="Enabled" /> : <TranslatedText text="Disabled" />}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label className="font-medium"><TranslatedText text="Auto Backup" /></Label>
                          <p className="text-sm text-muted-foreground"><TranslatedText text="Automatically backup data" /></p>
                        </div>
                        <Badge variant={selectedCompany.settings?.autoBackup ? 'default' : 'secondary'}>
                          {selectedCompany.settings?.autoBackup ? <TranslatedText text="Enabled" /> : <TranslatedText text="Disabled" />}
                        </Badge>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}
            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={() => {setIsViewModalOpen(false); setSelectedCompany(null);}}>
                <X className="h-4 w-4 mr-2" />
                <TranslatedText text="Close" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PageLayout>
  );
}
